import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import styles from "../styles/pages/termsconditions.module.css";

const TermsConditions = () => {
    const canonicalUrl =
        "https://myonlineshopping.co.in/terms-and-conditions";

    const sections = [
        {
            id: "acceptance",
            number: "01",
            title: "Acceptance of Terms",
            text: [
                "By accessing or using Mohit's Shopping App, you agree to comply with these Terms and Conditions.",
                "If you do not agree with these terms, please discontinue using the website.",
            ],
        },
        {
            id: "website",
            number: "02",
            title: "Nature of Our Website",
            text: [
                "Mohit's Shopping App is a product discovery and affiliate marketing website.",
                "We may display information about products available from Amazon and provide links that direct users to Amazon.",
            ],
        },
        {
            id: "affiliate",
            number: "03",
            title: "Amazon Affiliate Relationship",
            text: [
                "We may participate in the Amazon Associates Program and may earn a qualifying commission from eligible purchases made through affiliate links.",
                "The use of an affiliate link does not mean that Mohit's Shopping App is the seller of the product.",
            ],
        },
        {
            id: "purchases",
            number: "04",
            title: "Product Purchases",
            text: [
                "When you choose a product on our website and click an affiliate link, you are redirected to Amazon.",
                "You make your purchase voluntarily and directly through Amazon.",
                "Mohit's Shopping App does not take ownership of the product, process the Amazon checkout, collect payment for Amazon orders, or create the order on your behalf.",
            ],
        },
        {
            id: "prices",
            number: "05",
            title: "Prices & Product Information",
            text: [
                "Product prices, discounts, availability, specifications, images, seller information, and delivery estimates may change.",
                "The information displayed on Amazon at the time of purchase should be treated as the final source for purchase-related information.",
            ],
        },
        {
            id: "payment",
            number: "06",
            title: "Payment",
            text: [
                "Mohit's Shopping App does not process payments for products purchased through Amazon.",
                "Payment is completed directly through Amazon using the payment methods made available by Amazon.",
            ],
        },
        {
            id: "shipping",
            number: "07",
            title: "Shipping & Delivery",
            text: [
                "Shipping, delivery, courier services, estimated delivery dates, shipping charges, and order tracking are handled by Amazon and/or the applicable seller and delivery service.",
                "Please contact Amazon directly for issues relating to an Amazon order.",
            ],
        },
        {
            id: "returns",
            number: "08",
            title: "Returns, Refunds & Replacements",
            text: [
                "Mohit's Shopping App does not process Amazon returns, refunds, replacements, or cancellations.",
                "If you purchase a product from Amazon through our website and later need a return, refund, replacement, cancellation, or order-related assistance, please coordinate directly with Amazon using the options available with your Amazon order.",
            ],
        },
        {
            id: "responsibility",
            number: "09",
            title: "User Responsibility",
            text: [
                "Users are responsible for reviewing the final product details, price, seller information, delivery information, applicable terms, and return conditions on Amazon before completing a purchase.",
            ],
        },
        {
            id: "third-party",
            number: "10",
            title: "Third-Party Websites",
            text: [
                "Our website may contain links to Amazon and other third-party websites.",
                "We do not control third-party websites, services, policies, availability, or content.",
            ],
        },
        {
            id: "responsibility-limit",
            number: "11",
            title: "Limitation of Responsibility",
            text: [
                "We provide product discovery and referral services. We do not control Amazon's sellers, inventory, prices, shipping operations, payment systems, or customer service.",
                "Issues relating to an Amazon transaction should be handled through Amazon's available support and order-management channels.",
            ],
        },
        {
            id: "changes",
            number: "12",
            title: "Changes to These Terms",
            text: [
                "We may update these Terms and Conditions when necessary. Updated terms will be published on this page.",
            ],
        },
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Terms and Conditions - Mohit's Shopping App",
        description:
            "Terms and Conditions for using Mohit's Shopping App, an Amazon affiliate shopping website.",
        url: canonicalUrl,
        isPartOf: {
            "@type": "WebSite",
            name: "Mohit's Shopping App",
            url: "https://myonlineshopping.co.in/",
        },
    };

    return (
        <>
            <Helmet>
                <title>Terms & Conditions | Mohit's Shopping App</title>

                <meta
                    name="description"
                    content="Read the Terms and Conditions for using Mohit's Shopping App, including Amazon affiliate links, purchases, product information, and third-party services."
                />

                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large"
                />

                <meta name="author" content="Mohit Singh" />

                <link rel="canonical" href={canonicalUrl} />

                <meta property="og:type" content="website" />

                <meta
                    property="og:title"
                    content="Terms & Conditions | Mohit's Shopping App"
                />

                <meta
                    property="og:description"
                    content="Terms and Conditions for Mohit's Shopping App and its Amazon affiliate shopping links."
                />

                <meta property="og:url" content={canonicalUrl} />

                <meta
                    property="og:image"
                    content="https://myonlineshopping.co.in/favicon.png"
                />

                <meta name="twitter:card" content="summary_large_image" />

                <meta
                    name="twitter:title"
                    content="Terms & Conditions | Mohit's Shopping App"
                />

                <meta
                    name="twitter:description"
                    content="Terms and Conditions for Mohit's Shopping App."
                />

                <meta
                    name="twitter:image"
                    content="https://myonlineshopping.co.in/favicon.png"
                />

                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>

            <main className={styles.termsPage}>
                {/* HERO */}
                <section className={styles.hero}>
                    <div className={styles.heroGlowOne}></div>
                    <div className={styles.heroGlowTwo}></div>

                    <Container>
                        <div className={styles.breadcrumb}>
                            <span>Home</span>
                            <i>/</i>
                            <span>Legal</span>
                            <i>/</i>
                            <strong>Terms & Conditions</strong>
                        </div>

                        <div className={styles.heroContent}>
                            <div className={styles.heroText}>
                                <div className={styles.eyebrow}>
                                    <span className={styles.eyebrowIcon}>
                                        ✓
                                    </span>
                                    Website Legal Information
                                </div>

                                <h1>
                                    Terms &
                                    <span> Conditions</span>
                                </h1>

                                <p>
                                    Everything you need to know about using
                                    Mohit's Shopping App, discovering products,
                                    and shopping through our affiliate links.
                                </p>

                                <div className={styles.heroMeta}>
                                    <div className={styles.metaItem}>
                                        <span className={styles.metaIcon}>
                                            ↻
                                        </span>

                                        <div>
                                            <small>Last updated</small>
                                            <strong>August 26, 2026</strong>
                                        </div>
                                    </div>

                                    <div className={styles.metaDivider}></div>

                                    <div className={styles.metaItem}>
                                        <span className={styles.metaIcon}>
                                            ◉
                                        </span>

                                        <div>
                                            <small>Reading time</small>
                                            <strong>6 minutes</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.heroVisual}>
                                <div className={styles.documentCard}>
                                    <div className={styles.documentTop}>
                                        <div className={styles.docLogo}>
                                            MS
                                        </div>

                                        <div className={styles.docLines}>
                                            <span></span>
                                            <span></span>
                                        </div>

                                        <div className={styles.checkCircle}>
                                            ✓
                                        </div>
                                    </div>

                                    <div className={styles.docTitle}>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>

                                    <div className={styles.docContent}>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>

                                    <div className={styles.docFooter}>
                                        <span>TERMS</span>
                                        <span>2026</span>
                                    </div>
                                </div>

                                <div className={styles.floatingBadge}>
                                    <span>✓</span>
                                    Secure & Transparent
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* CONTENT */}
                <Container className={styles.contentContainer}>
                    <div className={styles.layout}>
                        {/* SIDEBAR */}
                        <aside className={styles.sidebar}>
                            <div className={styles.sidebarInner}>
                                <div className={styles.sidebarTitle}>
                                    <span>Contents</span>
                                    <small>12 sections</small>
                                </div>

                                <nav className={styles.toc}>
                                    {sections.map((section) => (
                                        <a
                                            key={section.id}
                                            href={`#${section.id}`}
                                        >
                                            <span>{section.number}</span>
                                            <strong>{section.title}</strong>
                                        </a>
                                    ))}
                                </nav>

                                <div className={styles.helpCard}>
                                    <div className={styles.helpIcon}>
                                        ?
                                    </div>

                                    <strong>Need help?</strong>

                                    <p>
                                        Have a question about these terms?
                                        We're happy to help.
                                    </p>

                                    <a href="https://myonlineshopping.co.in/contact-us">
                                        Contact us
                                        <span>→</span>
                                    </a>
                                </div>
                            </div>
                        </aside>

                        {/* MAIN ARTICLE */}
                        <article className={styles.article}>
                            {/* AFFILIATE NOTICE */}
                            <div className={styles.notice}>
                                <div className={styles.noticeIcon}>
                                    $
                                </div>

                                <div className={styles.noticeContent}>
                                    <span className={styles.noticeLabel}>
                                        Important disclosure
                                    </span>

                                    <h2>We are an Amazon affiliate website</h2>

                                    <p>
                                        Mohit's Shopping App does not directly
                                        sell Amazon products. When you click a
                                        product link and make an eligible
                                        purchase on Amazon, we may earn a
                                        qualifying commission at no additional
                                        cost to you.
                                    </p>
                                </div>

                                <div className={styles.noticeArrow}>
                                    ↗
                                </div>
                            </div>

                            {/* SECTIONS */}
                            {sections.map((section) => (
                                <section
                                    key={section.id}
                                    id={section.id}
                                    className={styles.termsSection}
                                >
                                    <div className={styles.sectionNumber}>
                                        {section.number}
                                    </div>

                                    <div className={styles.sectionBody}>
                                        <h2>{section.title}</h2>

                                        {section.text.map(
                                            (paragraph, index) => (
                                                <p key={index}>
                                                    {paragraph}
                                                </p>
                                            )
                                        )}
                                    </div>
                                </section>
                            ))}

                            {/* CONTACT */}
                            <section
                                id="contact"
                                className={styles.contactSection}
                            >
                                <div className={styles.contactTop}>
                                    <div>
                                        <span className={styles.contactLabel}>
                                            GET IN TOUCH
                                        </span>

                                        <h2>Still have questions?</h2>

                                        <p>
                                            If you have any questions about our
                                            website, affiliate links, or these
                                            terms, feel free to contact us.
                                        </p>
                                    </div>

                                    <div className={styles.contactSymbol}>
                                        ✦
                                    </div>
                                </div>

                                <div className={styles.contactActions}>
                                    <a
                                        href="mailto:mahi8191singh@gmail.com"
                                        className={styles.contactButton}
                                    >
                                        <span className={styles.buttonIcon}>
                                            ✉
                                        </span>

                                        <span>
                                            <small>Email us</small>
                                            <strong>
                                                mahi8191singh@gmail.com
                                            </strong>
                                        </span>

                                        <b>→</b>
                                    </a>

                                    <a
                                        href="tel:+918191042065"
                                        className={styles.contactButton}
                                    >
                                        <span className={styles.buttonIcon}>
                                            ☎
                                        </span>

                                        <span>
                                            <small>Call us</small>
                                            <strong>
                                                +91 8191042065
                                            </strong>
                                        </span>

                                        <b>→</b>
                                    </a>
                                </div>
                            </section>

                            <div className={styles.bottomNote}>
                                <span>✓</span>
                                By continuing to use our website, you
                                acknowledge that you have read and understood
                                these Terms & Conditions.
                            </div>
                        </article>
                    </div>
                </Container>
            </main>
        </>
    );
};

export default TermsConditions;
